"use client";

import { useEffect, useState } from "react";
import { LuMapPin, LuX } from "react-icons/lu";
import { AttendanceStatus, Prisma } from "@prisma/client";

import { env } from "~/env.mjs";

import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "~/components/v3/shadcn/Sheet";
import { toastHandler } from "~/components/ToastHandler";

import { getOS, getGoogleTicketURL } from "../actions";
import Link from "next/link";
import { DateTime } from "luxon";
import { useWhiteLabel } from "~/app/providers";
import { cn } from "~/utils/fontsConfig";

type EventType = Prisma.EventGetPayload<{
    select: {
        id: true;
        name: true;
        startTime: true;
        location: true;
        image: true;
        googleMapsPlaceName: true;
        slug: true;
        timezone: true;
        community: {
            select: {
                id: true;
                name?: true;
                image: true;
                slug: true;
            };
        };
        user: {
            select: {
                id: true;
                name: true;
                image: true;
            };
        };
        hosts: {
            select: {
                user: {
                    select: {
                        name: true;
                    };
                };
            };
        };
    };
}> & {
    ticketsPurchased: {
        quantity: number;
        eventId: string;
        price: number | null;
    }[];
    walletName?: "Apple" | "Google" | "Desktop";
};

type UserTicketAttendee = {
    userId: string;
    user: {
        id: string;
        name: string | null;
        image: string | null;
        email: string | null;
    };
    rsvpdAt: Date | null;
    createdAt: Date;
    status: AttendanceStatus;
    id: string;
};

type Props = {
    event: EventType;
    attendee: UserTicketAttendee;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
};

export const UserTicketSheet = ({
    event,
    attendee,
    isOpen,
    setIsOpen,
}: Props) => {
    const customization = useWhiteLabel()?.customization;
    const [wallet, setWallet] = useState<"Apple" | "Google" | "Desktop" | null>(
        event.walletName ?? null,
    );

    useEffect(() => {
        const getWallet = async () => {
            const wallet = await getOS();
            if (wallet) setWallet(wallet);
        };

        getWallet();
    }, [attendee]);

    const ticketsByPrice = event.ticketsPurchased.reduce(
        (acc: Record<string, number>, ticket) => {
            const price = ticket.price ?? "Free";
            acc[price] = (acc[price] || 0) + ticket.quantity;

            return acc;
        },
        {},
    );

    const startTime = DateTime.fromJSDate(event.startTime, { zone: "UTC" })
        .setZone(event.timezone, { keepLocalTime: true })
        .toJSDate();

    const startTimeStr = startTime.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
    });

    const rsvpdAt = (attendee.rsvpdAt ?? attendee.createdAt)?.toLocaleString(
        "en-US",
        {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: true,
        },
    );

    const qrCodeData = encodeURIComponent(`rvq.${attendee.user.id}.u`);

    return (
        <Sheet open={isOpen} onOpenChange={() => setIsOpen(false)}>
            <SheetContent
                showCloseIcon={false}
                className={cn(
                    "flex h-full w-full flex-col overflow-y-auto",
                    "rounded-br-md rounded-tr-md border-none",
                    customization?.mainCardClassName || "bg-v3-N600",
                    "p-4 md:min-w-[350px] md:p-6",
                )}
            >
                <SheetHeader className="flex-none py-2">
                    <div className="flex w-full items-center justify-between">
                        <SheetTitle
                            className={cn(
                                "text-lg",
                                customization?.labelClassName,
                            )}
                        >
                            Ticket details
                        </SheetTitle>
                        <SheetClose className="focus:ring-ring rounded-sm opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                            <LuX className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </div>
                </SheetHeader>

                <div className="flex flex-col gap-2">
                    <div
                        className={cn(
                            "flex w-full flex-col gap-6 overflow-hidden rounded-lg p-6",
                            customization?.secondaryCardClassName ||
                                "bg-gradient-to-b from-indigo-900 to-indigo-800",
                            customization?.descriptionClassName,
                        )}
                    >
                        <div className="flex items-center justify-between">
                            <div className="h-12 w-12 rounded-full">
                                {event.community?.image ? (
                                    <img
                                        src={`${env.NEXT_PUBLIC_ASSETS_URL}/community/${event.community.id}/${event.community.image}`}
                                        alt={
                                            event.community.name ?? "Community"
                                        }
                                        className="h-full w-full rounded-full object-cover"
                                    />
                                ) : event.user?.image ? (
                                    <img
                                        src={`${event.user.image}`}
                                        alt={event.user.name ?? "Profile"}
                                        className="h-full w-full rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="h-full w-full rounded-full bg-indigo-500" />
                                )}
                            </div>

                            {event.ticketsPurchased.length > 0 ? (
                                <span className="flex flex-col items-center gap-1">
                                    {Object.keys(ticketsByPrice).map(
                                        (price) => (
                                            <span className="rounded-full bg-white/20 px-4 py-1 text-sm text-white">
                                                <span
                                                    key={price}
                                                    className="flex items-center gap-1"
                                                >
                                                    <span className="text-white/50 ">
                                                        {ticketsByPrice[price]}x
                                                    </span>{" "}
                                                    <span className="text-white/50">
                                                        {price === "Free"
                                                            ? "Free"
                                                            : `($${price})`}
                                                    </span>
                                                </span>
                                            </span>
                                        ),
                                    )}
                                </span>
                            ) : (
                                <span className="rounded-full bg-white/20 px-4 py-1 text-sm text-white/50">
                                    Free
                                </span>
                            )}
                        </div>

                        <div className="flex flex-col gap-3">
                            <div className="flex flex-col gap-3">
                                <div className="text-sm">{startTimeStr}</div>
                                <h3
                                    className={cn(
                                        "text-xl",
                                        customization?.labelClassName ||
                                            "text-white",
                                    )}
                                >
                                    {event.name}
                                </h3>
                            </div>

                            <div
                                className={cn(
                                    "flex items-center gap-1 text-sm",
                                    customization?.descriptionClassName ||
                                        "text-white",
                                )}
                            >
                                <LuMapPin className="h-4 w-4" />
                                <span>{event.location}</span>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="text-sm">{attendee.user.name}</div>

                            <div className="text-sm ">{rsvpdAt}</div>
                        </div>
                    </div>

                    <div className="flex justify-center">
                        <div
                            className={cn(
                                "flex w-full flex-col items-center justify-center",
                                "gap-6 rounded-lg p-8",
                                customization?.secondaryCardClassName ||
                                    "bg-v3-N-500",
                            )}
                        >
                            <img
                                className="aspect-square w-full max-w-[250px] rounded-lg bg-white p-4"
                                src={`/api/og/qr?size=200&data=${qrCodeData}`}
                                alt="QR Code"
                            />

                            <div className="flex w-full justify-center gap-4 border-t border-v3-N450 pt-6">
                                {["Google", "Desktop"].includes(
                                    wallet ?? "",
                                ) && (
                                    <GoogleWalletButton
                                        attendee={attendee}
                                        event={{ ...event, startTime }}
                                    />
                                )}
                                {["Apple", "Desktop"].includes(
                                    wallet ?? "",
                                ) && (
                                    <AppleWalletButton
                                        attendee={attendee}
                                        event={event}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

const GoogleWalletButton = (props: {
    attendee: UserTicketAttendee;
    event: EventType;
}) => {
    if (!props.attendee.user.email) return false;

    const handleAddToGoogleWallet = () => {
        toastHandler({
            loadingMessage: "Adding to Google Wallet...",
            successMessage: "Added to Google Wallet",
            operation: async () => {
                const url = await getGoogleTicketURL(
                    {
                        id: props.attendee.id,
                        userId: props.attendee.user.id,
                        name: props.attendee.user.name ?? "Guest",
                    },
                    {
                        name: props.event.name,
                        date: props.event.startTime.toLocaleDateString(
                            "en-US",
                            {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                            },
                        ),
                        time: props.event.startTime.toLocaleTimeString(
                            "en-US",
                            {
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                            },
                        ),
                        place: props.event.googleMapsPlaceName ?? "N/A",
                        hostname: props.event.hosts[0]?.user.name ?? "N/A",
                        image: props.event.image,
                        id: props.event.id,
                        slug: props.event.slug,
                    },
                );

                window.open(url, "_blank");
            },
        });
    };

    return (
        <button
            onClick={handleAddToGoogleWallet}
            className="flex cursor-pointer justify-center"
        >
            <img
                src="/wallets/enUS_add_to_google_wallet_wallet-button.svg"
                alt="Add to Google Wallet"
                className="h-[36px] w-auto"
            />
        </button>
    );
};

const AppleWalletButton = (props: {
    attendee: UserTicketAttendee;
    event: EventType;
}) => {
    return (
        <Link
            href={`/events/${props.event.slug}/ticket-apple?aid=${props.attendee.id}`}
        >
            <img
                src="/wallets/US-UK_Add_to_Apple_Wallet_RGB_101421.svg"
                alt="Add to Apple Wallet"
                className="h-[40px] w-auto"
            />
        </Link>
    );
};
